// src/utils/mapProductToVM.js
import { getProductImage } from "./getProductImage";

/**
 * mapProductToVM
 * ------------------------------------------------------
 * 🔥 SOURCE UNIQUE DE VÉRITÉ FRONT
 *
 * Transforme un produit brut backend + audit pricing
 * en ViewModel exploitable UI (React).
 *
 * ⚠️ CONTRAINTES CRITIQUES :
 * - Tous les prix sont en CENTIMES (entiers)
 * - Ne JAMAIS casser la logique pricing existante
 * - Toujours fallback safe (backend legacy instable)
 * - Compatible enrichissements optionnels (ex: type_lait)
 *
 * @param {Object} p      Produit brut backend (MySQL / PHP)
 * @param {Object} audit  Résultat pricing engine (optionnel)
 */
export const mapProductToVM = (p, audit) => {
  if (!p) return null;

  // =====================================================
  // 🔢 NORMALISATION BACKEND (CENTIMES)
  // =====================================================
  // ⚠️ Historique legacy:
  // - price / base_price / public_price mélangés
  // - parfois string, parfois float
  // 👉 on force en entier (centimes)
  const priceCents = Math.round(
    Number(p.price ?? p.base_price ?? p.public_price ?? 0)
  );

  const stock = Number(p.stock ?? 0);

  // ⚠️ backend peut envoyer bool / int / string
  const isOnline =
    p.isOnline === true || p.isOnline === 1 || p.isOnline === "1";

  const isDiscount =
    p.isDiscount === true || p.isDiscount === 1 || p.isDiscount === "1";

  // état stock fiable UI
  const isInStock = Number.isFinite(stock) ? stock > 0 : false;

  // =====================================================
  // 🖼 IMAGE + META PRODUIT
  // =====================================================
  // fallback catégorie (legacy "categorie")
  const category = p.category || p.categorie || "Non renseigné";

  const imageUrl = getProductImage(p.image);

  // =====================================================
  // 🥛 ENRICHISSEMENT : TYPE DE LAIT (OPTIONNEL)
  // =====================================================
  // ⚠️ IMPORTANT :
  // - champ ajouté via LEFT JOIN backend
  // - peut être NULL / undefined
  // - ne doit JAMAIS casser le flux existant
  //
  // 👉 normalisation string safe
  const typeLait = typeof p.type_lait === "string" ? p.type_lait : "";

  // =====================================================
  // 🧠 AUDIT SAFETY LAYER (PRICING ENGINE)
  // =====================================================
  // ⚠️ L'audit peut être absent ou partiel
  const safeAudit = audit || {};

  // prix de base affiché
  const basePriceVal =
    Number(safeAudit.displayBasePrice) > 0
      ? Math.round(Number(safeAudit.displayBasePrice))
      : priceCents;

  // prix affiché final
  const displayPriceVal =
    Number(safeAudit.displayPrice) > 0
      ? Math.round(Number(safeAudit.displayPrice))
      : priceCents;

  // prix membre éventuel
  const memberPriceVal =
    Number(safeAudit.targetPrice) > 0
      ? Math.round(Number(safeAudit.targetPrice))
      : null;

  // =====================================================
  // 💵 CONVERSION UI (CHF)
  // =====================================================
  // ⚠️ Toujours diviser par 100 ici (et uniquement ici)
  const basePriceCHF = basePriceVal / 100;
  const displayPriceCHF = displayPriceVal / 100;
  const memberPriceCHF = memberPriceVal !== null ? memberPriceVal / 100 : null;

  // =====================================================
  // 🛒 LOGIQUE DE VENTE UI
  // =====================================================
  // Vendable = produit online ET en stock
  const isVendableUI = isOnline && isInStock;

  // =====================================================
  // 🚀 VIEWMODEL FINAL (UTILISÉ PARTOUT)
  // =====================================================
  return {
    ...p, // ⚠️ conserve compat legacy (IMPORTANT)

    // -----------------------------
    // 🔑 IDENTIFIANTS
    // -----------------------------
    id: Number(p.id),

    // -----------------------------
    // 📦 META PRODUIT
    // -----------------------------
    category,
    stock,
    isOnline,
    isDiscount,

    // -----------------------------
    // 📊 ÉTATS UI
    // -----------------------------
    isInStock,
    isVendableUI,

    // -----------------------------
    // 🖼 MEDIA
    // -----------------------------
    imageUrl,

    // -----------------------------
    // 🥛 NOUVEAU : TYPE DE LAIT
    // -----------------------------
    // 👉 utilisé pour:
    // - badge produit
    // - filtres boutique
    // 👉 jamais bloquant
    type_lait: typeLait,

    // -----------------------------
    // 💰 PRICING (CENTIMES - SOURCE)
    // -----------------------------
    basePrice: basePriceVal,
    displayPrice: displayPriceVal,
    memberPrice: memberPriceVal,

    // -----------------------------
    // 💵 PRICING (CHF - UI)
    // -----------------------------
    basePriceCHF,
    displayPriceCHF,
    memberPriceCHF,

    // -----------------------------
    // 🎯 LOGIQUE MARKETING
    // -----------------------------
    showUpsell: safeAudit.marketingIntent === "ACCOUNT_UPSELL",

    addToCartLabel:
      safeAudit.pricingMode === "MEMBER_ACTIVE"
        ? "AJOUTER AU PANIER"
        : "ACHETER AU PRIX PUBLIC",

    pricingMode: safeAudit.pricingMode || "PUBLIC",

    // -----------------------------
    // 🧪 DEBUG / TRACE
    // -----------------------------
    _raw: p, // payload backend brut
    _audit: safeAudit, // résultat pricing engine
  };
};
